import React from 'react'
import {Link as RouteLink} from 'react-router-dom'
import PropTypes from 'prop-types'
import {Flex, LinkBox, LinkOverlay, useBreakpointValue, useStyleConfig} from '@chakra-ui/react'
import PDCTitle from '../../../components/pdc-title'
import usePDCResponsiveElement from '../../../hooks/use-pdc-responsive-element.js'
import {categoryUrlBuilder} from '@salesforce/retail-react-app/app/utils/url'
import {DYNAMIC_IMAGE_WIDTHS} from '../../../constants.js'
import useMultiSite from '../../../hooks/use-multi-site.js'
import PDCImage from '../../../components/pdc-image/index.jsx'

/**
 * PDCHomeCategoriesPushTile component.
 * @typedef {Object} PDCHomeCategoriesPushTileProps
 * @property {string} title - Title of the tile.
 * @property {string} desc - Description of the tile.
 * @property {string} link - Link of the tile.
 * @property {string} linkLabel - Label of the link.
 * @property {object} smallImage - Small image object.
 * @property {object} mediumImage - Medium image object.
 * @property {object} largeImage - Large image object.
 * @property {object} xlargeImage - Extra large image object.
 * @property {object} xxlargeImage - Extra extra large image object.
 * @property {string} imageTitle - Title of the image.
 * @property {string} imageAlt - Alt of the image.
 * @param {boolean} displayPriority - Should image be loaded with high priority.
 * @param {number} nbSlides - Number of slides.
 * @returns {React.ReactElement} - PDCHomeCategoriesPushTile component.
 */
export const PDCHomeCategoriesPushTile = ({
    category,
    title,
    titleHeadingLevel,
    titleFontColor,
    txtVerticalPos,
    smallImage,
    mediumImage,
    largeImage,
    xlargeImage,
    xxlargeImage,
    imageTitle,
    imageAlt,
    displayPriority,
    nbSlides,
    custom
}) => {
    const {buildUrl} = useMultiSite()

    // Primary URL used to wrap the component.
    const categoryUrl = buildUrl(categoryUrlBuilder({id: category}))

    const txtPosFlex = {
        'top left': 'flex-start',
        middle: 'center',
        'bottom left': 'flex-end'
    }

    // Get current image based on breakpoint
    const currentImage = useBreakpointValue(
        usePDCResponsiveElement({
            smallEl: custom?.smallImage?.disBaseLink || smallImage,
            mediumEl: custom?.mediumImage?.disBaseLink || mediumImage,
            largeEl: custom?.largeImage?.disBaseLink || largeImage,
            xlargeEl: custom?.xlargeImage?.disBaseLink || xlargeImage,
            xxlargeEl: custom?.xxlargeImage?.disBaseLink || xxlargeImage
        }),
        {
            fallback: undefined
        }
    )

    const styles = useStyleConfig('Link', {})
    styles.textDecoration = 'none'
    styles._hover = {
        textDecoration: 'none'
    }

    let imageWidths
    switch (nbSlides) {
        case 1:
            imageWidths = DYNAMIC_IMAGE_WIDTHS.full
            break
        case 2:
            imageWidths = DYNAMIC_IMAGE_WIDTHS.halfandfullonsmalldevice
            break
        case 3:
            imageWidths = DYNAMIC_IMAGE_WIDTHS.thirdandfullonsmalldevice
            break
        case 4:
            imageWidths = DYNAMIC_IMAGE_WIDTHS.quarterandfullonsmalldevice
            break
        default:
            imageWidths = DYNAMIC_IMAGE_WIDTHS.halfandfullonsmalldevice
            break
    }

    return (
        <LinkBox as="article" textAlign="center" textDecoration="none" height={'100%'}>
            <PDCImage
                currentImage={currentImage}
                imageWidths={imageWidths}
                imageAlt={imageAlt}
                imageTitle={imageTitle}
                imageProps={{height: '100%'}}
                displayPriority={displayPriority}
                title={title}
                skeletonProps={{aspectRatio: '57 / 70', width: '100%'}}
                position="relative"
                height={'100%'}
            />
            <Flex
                alignItems="flex-start"
                direction={'column'}
                justifyContent={txtPosFlex[txtVerticalPos]}
                position="absolute"
                top="0"
                left="0"
                width="100%"
                height="100%"
                p={[
                    `10px 10px ${txtVerticalPos === 'bottom left' ? '86px' : '10px'}`,
                    null,
                    `20px 20px ${txtVerticalPos === 'bottom left' ? '96px' : '20px'}`
                ]}
            >
                <LinkOverlay as={RouteLink} to={categoryUrl} {...styles}>
                    <PDCTitle
                        title={title}
                        titleFontColor={titleFontColor}
                        titleHeadingLevel={titleHeadingLevel}
                        textColor={'white'}
                        size={['5xl', null, null, '6xl']}
                    />
                </LinkOverlay>
            </Flex>
        </LinkBox>
    )
}

PDCHomeCategoriesPushTile.propTypes = {
    category: PropTypes.string,
    title: PropTypes.string.isRequired,
    titleHeadingLevel: PropTypes.string,
    titleFontColor: PropTypes.string,
    txtVerticalPos: PropTypes.string,
    smallImage: PropTypes.object.isRequired,
    mediumImage: PropTypes.object,
    largeImage: PropTypes.object,
    xlargeImage: PropTypes.object,
    xxlargeImage: PropTypes.object,
    imageTitle: PropTypes.string,
    imageAlt: PropTypes.string,
    displayPriority: PropTypes.bool,
    nbSlides: PropTypes.number,
    custom: PropTypes.object
}
